import type { Locale } from "@/lib/i18n/config";

/**
 * Human names for the storefront's section types.
 *
 * Deliberately NOT in `messages.ts`. That catalog is contractually a flat
 * `Record<string, string>` per namespace — `messages.spec.ts` walks it to prove
 * Arabic and English stay in step — and a nested map breaks that check. This is
 * a lookup keyed by a section TYPE rather than a message key, so it is a
 * different kind of thing anyway.
 *
 * A type with no entry falls back to the raw type rather than rendering blank:
 * a new section added to the schemas package should show up in this list
 * looking unfinished, not disappear from it.
 */
const LABELS: Record<Locale, Record<string, string>> = {
  ar: {
    "restaurant-header": "الترويسة",
    "restaurant-hero": "الواجهة",
    "restaurant-menu": "قائمة الطعام",
    "restaurant-rewards": "شريط الولاء",
    "restaurant-order-cta": "زر الطلب للاستلام",
    "restaurant-footer": "التذييل",
  },
  en: {
    "restaurant-header": "Masthead",
    "restaurant-hero": "Hero",
    "restaurant-menu": "Menu",
    "restaurant-rewards": "Rewards strip",
    "restaurant-order-cta": "Order-ahead button",
    "restaurant-footer": "Footer",
  },
};

export function sectionLabel(type: string, locale: Locale): string {
  return LABELS[locale]?.[type] ?? type;
}

/**
 * Sections the seller may neither hide nor reorder.
 *
 * Mirrors `locked` on the section schemas: the masthead, the menu and the
 * footer are the page. Hiding the menu would leave a restaurant with a
 * storefront that does not show its food, and the buyer refuses it anyway —
 * `restaurantSectionsOrFallback` falls back to the locked three when the menu
 * is missing, so hiding it here would produce a control that appears to work
 * and changes nothing.
 */
export const LOCKED_SECTION_TYPES = new Set([
  "restaurant-header",
  "restaurant-menu",
  "restaurant-footer",
]);

/**
 * Sections that draw a feature the platform has not built (loyalty, pickup).
 * They render in the theme preview carrying a badge and never on a live page,
 * so the toggle here is shown as informational rather than as a live switch.
 */
export const PREVIEW_ONLY_SECTION_TYPES = new Set([
  "restaurant-rewards",
  "restaurant-order-cta",
]);
